import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCachedAssessmentTemplates } from '../hooks/useCachedData';
import useAssessment from '../hooks/useAssessment';
import AssessmentFlow from '../components/AssessmentFlow/AssessmentFlow';
import Spinner from '../components/Spinner/Spinner';
import { getCachedProfile } from '../util/CacheUtil';
import { getUserProfile } from '../components/API';
import { FOCUS_LOOKUP, filterAssessmentsBySex } from '../data/categoryDefinitions';
import '../components/AssessmentFlow/AssessmentFlow.css';

export default function Assessment() {
    const { assessmentId } = useParams();
    const navigate = useNavigate();
    const { assessmentTemplates } = useCachedAssessmentTemplates();
    const {
        answers,
        summary,
        loading,
        submitting,
        loadSummary,
        setAnswer,
        clearAnswers,
        submitAssessment,
        hasExistingAssessment,
        getExistingDate,
    } = useAssessment();

    const [focuses, setFocuses] = useState(() => {
        const cached = getCachedProfile();
        return cached?.focuses || null;
    });
    const [sex, setSex] = useState(() => {
        const cached = getCachedProfile();
        return cached?.sex || null;
    });

    useEffect(() => {
        if (focuses !== null && sex !== null) return;
        getUserProfile()
            .then(data => {
                if (focuses === null) setFocuses(data.focuses || []);
                if (sex === null) setSex(data.sex || null);
            })
            .catch(() => { if (focuses === null) setFocuses([]); });
    }, [focuses, sex]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        clearAnswers();
    }, [assessmentId, clearAnswers]);

    const relevantIds = useMemo(() => {
        if (!focuses || focuses.length === 0) return null;
        const ids = new Set();
        for (const fId of focuses) {
            const focus = FOCUS_LOOKUP[fId];
            if (focus) {
                for (const aid of focus.assessment_ids) ids.add(aid);
            }
        }
        const filtered = filterAssessmentsBySex([...ids], sex);
        return new Set(filtered);
    }, [focuses, sex]);

    if (loading || !assessmentTemplates) {
        return (
            <div className="assessment-list-page" style={{ textAlign: 'center', paddingTop: 80 }}>
                <Spinner />
            </div>
        );
    }

    if (assessmentId) {
        const template = assessmentTemplates[assessmentId];
        if (!template) {
            return (
                <div className="assessment-list-page">
                    <h1>Assessment Not Found</h1>
                    <p>The assessment "{assessmentId}" does not exist.</p>
                    <button className="btn-pill" onClick={() => navigate('/assessment')}>
                        Back to Assessments
                    </button>
                </div>
            );
        }

        return (
            <AssessmentFlow
                template={template}
                answers={answers}
                onSetAnswer={setAnswer}
                onSubmit={() => submitAssessment(assessmentId)}
                submitting={submitting}
                existingDate={getExistingDate(assessmentId)}
                onCancel={() => navigate('/assessment')}
            />
        );
    }

    const templateEntries = Object.entries(assessmentTemplates)
        .filter(([id]) => !relevantIds || relevantIds.has(id));

    if (templateEntries.length === 0) {
        return (
            <div className="assessment-list-page">
                <h1>Health Assessments</h1>
                <p>Select focus areas to unlock relevant assessments.</p>
                <button className="btn-pill" onClick={() => navigate('/select-focus')}>
                    Choose Focus
                </button>
            </div>
        );
    }

    return (
        <div className="assessment-list-page">
            <h1>Health Assessments</h1>
            <p>Complete assessments to get personalized health insights.</p>

            <div className="assessment-grid">
                {templateEntries.map(([id, tmpl]) => (
                    <div
                        key={id}
                        className="assessment-card"
                        onClick={() => navigate(`/assessment/${id}`)}
                    >
                        <h3>{tmpl.display_name}</h3>
                        <p>{tmpl.description}</p>
                        <div className="assessment-card-meta">
                            <span>{tmpl.estimated_minutes} min</span>
                            {hasExistingAssessment(id) && (
                                <span className="assessment-card-completed">
                                    Completed
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
